import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { CustomerIndividual, CustomerOrganization } from 'types/Customer'

export interface UseCustomerFormProps {
    initialData?: CustomerIndividual | CustomerOrganization | null
    onClose?: () => void
}

export const useCustomerForm = ({ initialData, onClose }: UseCustomerFormProps) => {
    const [mode, setMode] = useState<'individual' | 'organization'>(
        initialData && 'taxNumber' in initialData ? 'organization' : 'individual',
    )

    const {
        control,
        handleSubmit: rhfHandleSubmit,
        formState: { errors },
        watch,
        setValue,
        trigger,
    } = useForm({
        defaultValues: {
            email: initialData?.email || '',
            confirmEmail: initialData?.confirmEmail || '',
            firstName: initialData?.firstName || '',
            lastName: initialData?.lastName || '',
            phoneNumber: initialData?.phoneNumber || '',
            country: initialData?.country || '',
            postCode: initialData?.postCode || '',
            city: initialData?.city || '',
            address: initialData?.address || '',
            taxNumber: mode === 'organization' ? (initialData as CustomerOrganization)?.taxNumber || '' : '',
            companyName: mode === 'organization' ? (initialData as CustomerOrganization)?.companyName || '' : '',
        },
    })

    const onSubmit = async (data: CustomerIndividual | CustomerOrganization) => {
        const customerData = {
            email: data.email.toLowerCase(),
            confirmEmail: data.confirmEmail,
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber,
            country: data.country,
            postCode: data.postCode,
            city: data.city,
            address: data.address,
            ...(mode === 'organization' && { taxNumber: (data as CustomerOrganization).taxNumber }),
            ...(mode === 'organization' && { companyName: (data as CustomerOrganization).companyName }),
        }

        try {
            if (initialData) {
                console.log('Updating customer:', customerData)
            } else {
                console.log('Creating new customer:', customerData)
            }
            onClose?.()
        } catch (error) {
            console.error('Error saving customer data:', error)
        }
    }

    const toggleMode = () => {
        setMode((prevMode) => {
            const newMode = prevMode === 'individual' ? 'organization' : 'individual'
            if (newMode === 'individual') setValue('taxNumber', '')
            return newMode
        })
    }

    const validateForm = async () => {
        const isValid = await trigger()
        if (!isValid) throw new Error('Form validation failed')
        return true
    }

    return {
        control,
        handleSubmit: rhfHandleSubmit(onSubmit),
        setValue,
        errors,
        mode,
        setMode: toggleMode,
        watch,
        validateForm,
    }
}
